import React from 'react';
import { useTranslation } from 'react-i18next';
import Pencil from '@/icons/Pencil';
import ShowAroundButton from '@/components/help/ShowAroundButton';

import './family-page-header.css';
import '@/components/help/help-tour.css';

export interface FamilyPageHeaderProps {
  familyDisplayName: string;
  sectionTitle: string;
  onEditFamilyName: () => void;
  /** When set, shows “Show me around” on the title row. */
  onStartTour?: () => void;
  /** Editorial intro text (may include ScreenHelpDot). */
  intro: React.ReactNode;
  /** Right-aligned actions on the intro row (below Show me around). */
  introTrailing?: React.ReactNode;
  /** Optional wrapper attributes for tour anchors. */
  titleTourId?: string;
  introTourId?: string;
  /** data-tour on the root header (e.g. members-intro covering title + lede). */
  rootTourId?: string;
  className?: string;
}

/**
 * Shared header for in-family section screens:
 * family name eyebrow → screen title (+ Show me around) → page intro (+ CTAs).
 */
const FamilyPageHeader: React.FC<FamilyPageHeaderProps> = ({
  familyDisplayName,
  sectionTitle,
  onEditFamilyName,
  onStartTour,
  intro,
  introTrailing,
  titleTourId,
  introTourId,
  rootTourId,
  className
}) => {
  const { t } = useTranslation('screens');
  const hasIntroTrailing = introTrailing != null && introTrailing !== false;

  return (
    <header className={['family-page-header', className].filter(Boolean).join(' ')} data-tour={rootTourId}>
      <div data-tour={titleTourId}>
        <p className="family-page-header__eyebrow">
          <button
            type="button"
            className="family-page-header__eyebrow-btn"
            onClick={onEditFamilyName}
            title={t('familyCard.renameTitle')}
            aria-label={t('familyCard.renameAria', { name: familyDisplayName })}
          >
            {familyDisplayName}
            <span className="family-page-header__edit" aria-hidden>
              <Pencil width={14} height={14} />
            </span>
          </button>
        </p>
        <div className="family-page-header__title-row">
          <h1 className="family-page-header__title">{sectionTitle}</h1>
          {onStartTour ? (
            <div className="family-page-header__title-trailing">
              <div data-tour="show-around">
                <ShowAroundButton onClick={onStartTour} />
              </div>
            </div>
          ) : null}
        </div>
      </div>

      <div
        className={`family-page-header__intro-row${hasIntroTrailing ? '' : ' family-page-header__intro-row--text-only'}`}
      >
        <p className="family-page-header__intro" data-tour={introTourId}>
          {intro}
        </p>
        {hasIntroTrailing ? <div className="family-page-header__intro-actions">{introTrailing}</div> : null}
      </div>
    </header>
  );
};

export default FamilyPageHeader;
